import { Locale } from "@dealer-center/ddc-core-utils";
import type {
  CreateUserErrorReason,
  DeleteUserErrorReason,
  ResetPasswordErrorReason,
  SaveUserInfoErrorReason
} from "@dealer-center/ddc-pages-configuration-interface";

/** Total over the reason union, so a new reason without copy is a type error. */
const SAVE_ERROR_LABELS: Record<SaveUserInfoErrorReason, string> = {
  LINKED_USER_IDENTITY: "UM_SAVE_ERR_LINKED",
  NOT_FOUND: "UM_SAVE_ERR_NOT_FOUND",
  VALIDATION: "UM_SAVE_ERR_INVALID",
  INVALID: "UM_SAVE_ERR_INVALID",
  UPSTREAM: "UM_SAVE_ERR_GENERIC"
};

/** `INVALID` is excluded: its copy is upstream's own message. */
const RESET_ERROR_LABELS: Record<
  Exclude<ResetPasswordErrorReason, "INVALID">,
  string
> = {
  LINKED_USER_PASSWORD: "UM_PW_ERR_LINKED",
  NOT_IN_ACCOUNT: "UM_PW_ERR_NOT_IN_ACCOUNT",
  NOT_FOUND: "UM_PW_ERR_NOT_FOUND",
  VALIDATION: "UM_PW_ERR_GENERIC",
  UPSTREAM: "UM_PW_ERR_GENERIC"
};

const CREATE_ERROR_LABELS: Record<
  Exclude<CreateUserErrorReason, "INVALID">,
  string
> = {
  USER_ID_IN_USE: "UM_ADD_ERR_IN_USE",
  TIMEOUT: "UM_ADD_ERR_TIMEOUT",
  VALIDATION: "UM_ADD_ERR_GENERIC",
  UPSTREAM: "UM_ADD_ERR_GENERIC"
};

const DELETE_ERROR_LABELS: Record<DeleteUserErrorReason, string> = {
  CANNOT_DELETE: "UM_DEL_ERR_CANNOT",
  NOT_FOUND: "UM_DEL_ERR_NOT_FOUND",
  VALIDATION: "UM_DEL_ERR_GENERIC",
  UPSTREAM: "UM_DEL_ERR_GENERIC"
};

/**
 * Read off `unknown`: a network `TypeError` or the shared 403 carries no `reason`, and an
 * unrecognized string must not index the tables.
 */
const knownReason = <TReason extends string>(
  error: unknown,
  labels: Record<TReason, string>
): TReason | undefined => {
  const reason =
    error && typeof error === "object" && "reason" in error
      ? error.reason
      : undefined;

  return typeof reason === "string" &&
    Object.prototype.hasOwnProperty.call(labels, reason)
    ? (reason as TReason)
    : undefined;
};

export const saveErrorMessage = (error: unknown): string => {
  const reason = knownReason(error, SAVE_ERROR_LABELS);

  return Locale.getText(
    reason ? SAVE_ERROR_LABELS[reason] : "UM_SAVE_ERR_GENERIC"
  ) as string;
};

/** An `INVALID` rejection shows upstream's message as-is (English-only, not localized). */
export const resetErrorMessage = (error: unknown): string => {
  if (
    error &&
    typeof error === "object" &&
    "reason" in error &&
    error.reason === "INVALID" &&
    "message" in error &&
    typeof error.message === "string" &&
    error.message
  ) {
    return error.message;
  }

  const reason = knownReason(error, RESET_ERROR_LABELS);

  return Locale.getText(
    reason ? RESET_ERROR_LABELS[reason] : "UM_PW_ERR_GENERIC"
  ) as string;
};

const invalidMessage = (error: unknown): string | undefined =>
  error &&
  typeof error === "object" &&
  "reason" in error &&
  error.reason === "INVALID" &&
  "message" in error &&
  typeof error.message === "string" &&
  error.message
    ? error.message
    : undefined;

/** An `INVALID` rejection shows upstream's message as-is, like the reset. */
export const createErrorMessage = (error: unknown): string => {
  const upstreamText = invalidMessage(error);
  if (upstreamText) {
    return upstreamText;
  }

  const reason = knownReason(error, CREATE_ERROR_LABELS);

  return Locale.getText(
    reason ? CREATE_ERROR_LABELS[reason] : "UM_ADD_ERR_GENERIC"
  ) as string;
};

export const isDeleteNotFound = (error: unknown): boolean =>
  knownReason(error, DELETE_ERROR_LABELS) === "NOT_FOUND";

export const deleteErrorMessage = (error: unknown): string => {
  const reason = knownReason(error, DELETE_ERROR_LABELS);

  return Locale.getText(
    reason ? DELETE_ERROR_LABELS[reason] : "UM_DEL_ERR_GENERIC"
  ) as string;
};
